import React, { useState } from 'react';
import type { Message, MessagePhoto } from '../../domain/conversations';
import type { OutgoingMessage } from '../../hooks/useOutbox';
import CollapsedItem from '../CollapsedItem';
import { ActionError } from '../PageStates';
import { PersonName } from '../PersonDialog';
import UserAvatar from '../UserAvatar';

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** A message photo at its own proportions, so the thread doesn't jump as it loads. */
function Photo({ photo }: { photo: MessagePhoto }) {
  const sized = photo.width && photo.height;
  return (
    <a href={photo.url} target="_blank" rel="noreferrer" className="block">
      <img
        src={photo.url}
        alt="Sent with the message"
        width={photo.width ?? undefined}
        height={photo.height ?? undefined}
        style={sized ? { aspectRatio: `${photo.width} / ${photo.height}` } : undefined}
        className="max-h-72 w-auto max-w-full rounded-2xl object-cover"
      />
    </a>
  );
}

/**
 * The messages I may read, oldest first (018 FR-014, FR-015): mine on the
 * right, others' on the left with their name in a group, notices centred.
 * A message from someone with a block either way stays collapsed (017).
 * Take back is mine alone and waits for the server.
 */
export default function MessageThread({
  messages,
  outgoing = [],
  myId,
  isGroup,
  onTakeBack,
  onRetry,
  onBlocked,
}: {
  messages: Message[];
  outgoing?: OutgoingMessage[];
  myId: string | null;
  isGroup: boolean;
  onTakeBack: (message: Message) => Promise<void>;
  onRetry: (key: string) => void;
  onBlocked?: () => void;
}) {
  const [takingBack, setTakingBack] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const takeBack = async (m: Message) => {
    setTakingBack(m.id);
    setError(null);
    try {
      await onTakeBack(m);
    } catch (e) {
      setError(e);
    } finally {
      setTakingBack(null);
    }
  };

  return (
    <div className="space-y-3">
      {error !== null && <ActionError error={error} />}
      <ol aria-label="Messages" className="flex flex-col gap-3">
        {messages.map((m) => {
          if (m.kind === 'system') {
            return (
              <li key={m.id} className="py-1 text-center text-xs text-muted-foreground">
                {m.body}
              </li>
            );
          }
          const mine = !!m.sender && m.sender.id === myId;
          const bubble = (
            <MessageBubble
              message={m}
              mine={mine}
              showSender={isGroup && !mine}
              onBlocked={onBlocked}
              action={
                mine &&
                !m.removed && (
                  <button
                    type="button"
                    className="text-xs font-medium text-muted-foreground hover:text-primary hover:underline disabled:opacity-50"
                    disabled={takingBack === m.id}
                    onClick={() => takeBack(m)}
                  >
                    Take back
                  </button>
                )
              }
            />
          );
          return (
            <li key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
              {m.isCollapsed ? (
                <div className="w-full max-w-[80%]">
                  <CollapsedItem kind="message">{bubble}</CollapsedItem>
                </div>
              ) : (
                bubble
              )}
            </li>
          );
        })}
        {outgoing.map((o) => (
          <li key={o.key} className="flex flex-col items-end">
            <div
              className={`max-w-[80%] rounded-3xl bg-primary px-4 py-2 text-primary-foreground ${
                o.state === 'sending' ? 'opacity-70' : 'opacity-60'
              }`}
            >
              {o.photoPreview && (
                <img
                  src={o.photoPreview}
                  alt="Sent with the message"
                  className="mb-1 max-h-72 rounded-2xl"
                />
              )}
              {o.body && <p className="whitespace-pre-wrap break-words">{o.body}</p>}
            </div>
            <p className="mt-1 text-xs text-muted-foreground" role="status">
              {o.state === 'sending' ? (
                'Sending…'
              ) : (
                <>
                  Not sent.{' '}
                  <button
                    type="button"
                    className="font-semibold text-primary hover:underline"
                    onClick={() => onRetry(o.key)}
                  >
                    Retry
                  </button>
                </>
              )}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function MessageBubble({
  message: m,
  mine,
  showSender,
  action,
  onBlocked,
}: {
  message: Message;
  mine: boolean;
  showSender: boolean;
  action: React.ReactNode;
  onBlocked?: () => void;
}) {
  return (
    <div className={`flex max-w-[80%] gap-2 ${mine ? 'flex-row-reverse' : ''}`}>
      {showSender &&
        (m.sender ? (
          <UserAvatar person={m.sender} className="size-8" textClassName="text-xs" />
        ) : (
          <div className="size-8 shrink-0 rounded-full bg-muted" aria-hidden="true" />
        ))}
      <div className={`flex min-w-0 flex-col ${mine ? 'items-end' : 'items-start'}`}>
        {showSender && (
          <span className="px-2 text-xs font-semibold text-muted-foreground">
            {m.sender ? <PersonName person={m.sender} onBlocked={onBlocked} /> : 'Deleted account'}
          </span>
        )}
        <div
          className={`rounded-3xl px-4 py-2 ${
            mine ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground'
          }`}
        >
          {m.removed ? (
            <span className="italic opacity-80">[Message removed]</span>
          ) : (
            <div className="space-y-1">
              {m.photos.map((p) => (
                <Photo key={p.id} photo={p} />
              ))}
              {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
            </div>
          )}
        </div>
        <div className="mt-1 flex items-center gap-3 px-2">
          <time dateTime={m.sentAt} className="text-xs text-muted-foreground">
            {formatTime(m.sentAt)}
          </time>
          {action}
        </div>
      </div>
    </div>
  );
}
